import type { Container } from "pixi.js";
import type { WinMultiplierInput } from "./replay/ApplyWinMultiplierActionHandler";
import type { CannonView } from "./CannonView";
import type { CannonBulletView } from "./CannonBulletView";
import { CANNON_TUNING } from "./cannonTuning";
import type { Log, PresentationTaskRunner } from "@slot/presentation";

/**
 * CannonPresenter — drives the {@link CannonView} from the server's `apply_win_multiplier` state
 * (a GAME-DEFINED action dispatched by `replay/ApplyWinMultiplierActionHandler`).
 *
 *   - reveal (no `firedMultiplier`): render the loadout (the chamber and the queue, one round per placeholder
 *     bone), silently.
 *   - winning substep (`firedMultiplier` set): the OLD chamber fires, then the queue advances.
 *
 * SERVER-AUTHORITATIVE values; win MONEY rides on `grid_win_highlight`. View null → every call no-ops.
 * Each shot of a spin climbs the fire-sound ladder (`CANNON_TUNING.fireSoundIds`); `resetShotSequence`
 * starts the next spin from its first sound.
 */
export class CannonPresenter {
  private readonly view: CannonView | null;
  private readonly log: Log;
  private readonly playSound: (soundId: string) => void;
  private readonly baseLadder: readonly string[];
  private shotsThisSpin = 0;

  public constructor(input: {
    view: CannonView | null;
    createBullet: (() => CannonBulletView) | null;
    queueShine?: ((bulletRoot: Container) => void) | null;
    baseLadder: readonly string[];
    runner: PresentationTaskRunner;
    playSound: (soundId: string) => void;
    log: Log;
  }) {
    this.view = input.view;
    this.log = input.log;
    this.playSound = input.playSound;
    this.baseLadder = input.baseLadder;
    this.view?.configure({ runner: input.runner, createBullet: input.createBullet, queueShine: input.queueShine ?? null });
    // Boot-init: show the base ladder from the first frame (before any spin).
    this.loadBaseLadder();
  }

  /** (Re)load the base-game ladder — boot + each BASE spin REQUEST. Silent replace. */
  public loadBaseLadder(): void {
    if (!this.view) return;
    this.view.setLoadout(this.baseLadder.slice(0, CANNON_TUNING.spine.placeholders.length));
  }

  /** A new spin (base or free): the next shot plays the ladder's first sound. */
  public resetShotSequence(): void {
    this.shotsThisSpin = 0;
  }

  /** @param onImpact fired the moment the blast lands — the game morphs the win label with it. */
  public async presentWinMultiplier(input: WinMultiplierInput, onImpact?: () => void | Promise<void>): Promise<void> {
    if (!this.view) {
      await onImpact?.();
      return;
    }
    const loadout = [input.chamberedMultiplier, ...input.upcomingMultipliers].slice(0, CANNON_TUNING.spine.placeholders.length);
    if (input.firedMultiplier !== undefined) {
      this.log(`[Cannon] fire x${input.firedMultiplier} → chamber x${input.chamberedMultiplier}`);
      const fireSoundIds = CANNON_TUNING.fireSoundIds;
      this.playSound(fireSoundIds[Math.min(this.shotsThisSpin, fireSoundIds.length - 1)]!);
      this.shotsThisSpin++;
      await this.view.playFire(input.firedMultiplier, onImpact);
      this.view.applyPostFireLoadout(loadout);
    } else {
      this.log(`[Cannon] load x${input.chamberedMultiplier} (+${input.upcomingMultipliers.length} queued)`);
      this.view.setLoadout(loadout);
    }
  }
}
